import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Typography, Avatar, Upload } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, UploadOutlined } from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import './Settings.css';

const { Title, Text } = Typography;

const Settings = () => {
  const { currentUser, token } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (currentUser) {
      form.setFieldsValue({
        first_name: currentUser.first_name || '',
        last_name: currentUser.last_name || '',
        email: currentUser.email || '',
        phone: currentUser.phone || '',
        company_name: currentUser.company_name || '',
        display_name: currentUser.display_name || currentUser.name || ''
      });
    }
  }, [currentUser, form]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/users/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(values)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update profile');
      }

      message.success('Profile updated successfully!');
      
      // Optionally refresh the page or update context
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Error updating profile:', error);
      message.error(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (info) => {
    if (info.file.status === 'uploading') {
      setUploading(true);
      return;
    }
    
    if (info.file.status === 'done') {
      setUploading(false);
      message.success('Profile picture updated successfully!');
      // Refresh to show new avatar
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else if (info.file.status === 'error') {
      setUploading(false);
      message.error('Failed to upload profile picture');
    }
  };

  const uploadProps = {
    name: 'avatar',
    action: `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/users/avatar`,
    headers: {
      authorization: `Bearer ${token}`,
    },
    onChange: handleAvatarUpload,
    showUploadList: false
  };

  const getAvatarUrl = () => {
    if (currentUser?.profile_picture_url) {
      return currentUser.profile_picture_url.startsWith('http') 
        ? currentUser.profile_picture_url 
        : `${window.location.origin}${currentUser.profile_picture_url}`;
    }
    return null;
  };

  const getInitials = () => {
    if (currentUser?.first_name && currentUser?.last_name) {
      return `${currentUser.first_name[0]}${currentUser.last_name[0]}`.toUpperCase();
    }
    if (currentUser?.display_name || currentUser?.name) {
      const name = currentUser.display_name || currentUser.name;
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return 'U';
  };

  return (
    <div className="reseller-settings">
      {/* Header */}
      <div className="settings-header">
        <div>
          <Title level={2} style={{ margin: 0 }}>Settings</Title>
          <Text type="secondary">ServiceTech Marketing Partners</Text>
        </div>
        <div className="last-updated">Last updated: {new Date().toLocaleTimeString()}</div>
      </div>

      {/* Page Title */}
      <div className="page-title-section">
        <Title level={3} style={{ margin: 0 }}>Profile Settings</Title>
        <Text type="secondary">Update your personal information and preferences</Text>
      </div>

      <Card className="settings-card">
        {/* Avatar Section */}
        <div className="avatar-section">
          <div className="avatar-container">
            {getAvatarUrl() ? (
              <Avatar size={100} src={getAvatarUrl()} />
            ) : (
              <Avatar size={100} style={{ backgroundColor: '#3b82f6', fontSize: 36 }}>
                {getInitials()}
              </Avatar>
            )}
          </div>
          <div className="avatar-info">
            <Title level={4} style={{ margin: 0 }}>
              {currentUser?.display_name || currentUser?.name || 'User'}
            </Title>
            <Text type="secondary">{currentUser?.email}</Text>
            <div style={{ marginTop: 12 }}>
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />} loading={uploading}>
                  Change Profile Picture
                </Button>
              </Upload>
            </div>
          </div>
        </div>

        {/* Profile Form */}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="profile-form"
        >
          <div className="form-section">
            <Title level={5}>Personal Information</Title>
            
            <div className="form-row">
              <Form.Item
                label="First Name"
                name="first_name"
                rules={[{ required: true, message: 'Please enter your first name' }]}
              >
                <Input prefix={<UserOutlined />} placeholder="First Name" />
              </Form.Item>

              <Form.Item
                label="Last Name"
                name="last_name"
                rules={[{ required: true, message: 'Please enter your last name' }]}
              >
                <Input prefix={<UserOutlined />} placeholder="Last Name" />
              </Form.Item>
            </div>

            <Form.Item
              label="Display Name"
              name="display_name"
              rules={[{ required: true, message: 'Please enter a display name' }]}
            >
              <Input prefix={<UserOutlined />} placeholder="Display Name" />
            </Form.Item>

            <div className="form-row">
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input prefix={<MailOutlined />} placeholder="Email" />
              </Form.Item>

              <Form.Item
                label="Phone"
                name="phone"
              >
                <Input prefix={<PhoneOutlined />} placeholder="Phone Number" />
              </Form.Item>
            </div>
          </div>

          <div className="form-section">
            <Title level={5}>Company Information</Title>
            
            <Form.Item
              label="Company Name"
              name="company_name"
            >
              <Input placeholder="Company Name" />
            </Form.Item>
          </div>

          <div className="form-actions">
            <Button onClick={() => form.resetFields()}>
              Reset
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Save Changes
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default Settings;
